import { GeoJSONSource, type Map as MaplibreMap } from "maplibre-gl";
import type { FeatureCollection, Point } from "geojson";

import { point } from "@turf/turf";

import { truncateGeoJson } from "../map";

/**
 * GPU-rendered rendering path for catchment/dataset pins.
 *
 * Instead of one `maplibregl.Marker` DOM element per row, the whole set is a single GeoJSON source drawn by three
 * symbol layers — icon, label, rank — that redraw in one GPU pass regardless of
 * feature count.
 * Draggable "by location" pins and the single search-location pin stay DOM `Marker`s.
 */

export const CATCHMENT_MARKER_SOURCE_ID = "catchment-marker-source";
export const CATCHMENT_MARKER_ICON_LAYER_ID = "catchment-marker-icon";
export const CATCHMENT_MARKER_LABEL_LAYER_ID = "catchment-marker-label";
export const CATCHMENT_MARKER_RANK_LAYER_ID = "catchment-marker-rank";
export const CATCHMENT_MARKER_IMAGE_ID = "catchment-marker-pin";

// Matches the DOM marker's rendered footprint (icons/marker.svg is 27×41).
const CATCHMENT_MARKER_WIDTH = 27;
const CATCHMENT_MARKER_HEIGHT = 41;
// Register at 2× and draw at 2× so the icon renders crisp at 27×41 (icon-size 1).
const CATCHMENT_MARKER_PIXEL_RATIO = 2;

// Text offsets (in ems, relative to text-size) place the label/rank above the
// bottom-anchored pin, mirroring the DOM markers' `bottom: 32px`/`48px` layout.
const LABEL_TEXT_OFFSET_Y = -4;
export const RANK_TEXT_OFFSET_WITH_LABEL = -5.5;
export const RANK_TEXT_OFFSET_WITHOUT_LABEL = -4;

const LABEL_TEXT_COLOR = "#000000";
const LABEL_HALO_COLOR = "#ffffff";

export type CatchmentMarkerFeatureInput = {
  id: string;
  coordinates: [number, number];
  label?: string;
  rank?: number;
};

/** Builds one `FeatureCollection` from the dataset rows. */
export const buildCatchmentMarkerFeatureCollection = (
  items: CatchmentMarkerFeatureInput[]
): FeatureCollection<Point> => ({
  type: "FeatureCollection",
  features: items.map(({ id, coordinates, label, rank }) => {
    const truncated = truncateGeoJson(point(coordinates)).geometry
      .coordinates as [number, number];

    const properties: { label: string; rank?: string } = { label: label ?? "" };
    if (rank != null) properties.rank = `#${rank}`;

    return {
      type: "Feature",
      id,
      geometry: { type: "Point", coordinates: truncated },
      properties
    };
  })
});

/**
 * Registers `icons/marker.svg` under `CATCHMENT_MARKER_IMAGE_ID` once, drawing it
 * onto a 2× canvas so the raster size is deterministic across browsers (SVG
 * intrinsic sizing is not). Runs `onReady` once the image is available (or
 * immediately if already registered). No-ops where the image API is unavailable
 * (e.g. tests).
 */
export const registerCatchmentMarkerImage = (
  map: MaplibreMap | null,
  iconUrl: string,
  onReady?: () => void
) => {
  if (!map || typeof map.addImage !== "function") return;
  if (map.hasImage?.(CATCHMENT_MARKER_IMAGE_ID)) {
    onReady?.();
    return;
  }

  const img = new Image();
  img.onload = () => {
    // Another render pass may have registered it while this image loaded.
    if (map.hasImage?.(CATCHMENT_MARKER_IMAGE_ID)) {
      onReady?.();
      return;
    }

    const width = CATCHMENT_MARKER_WIDTH * CATCHMENT_MARKER_PIXEL_RATIO;
    const height = CATCHMENT_MARKER_HEIGHT * CATCHMENT_MARKER_PIXEL_RATIO;
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      onReady?.();
      return;
    }
    ctx.drawImage(img, 0, 0, width, height);
    map.addImage(
      CATCHMENT_MARKER_IMAGE_ID,
      ctx.getImageData(0, 0, width, height),
      { pixelRatio: CATCHMENT_MARKER_PIXEL_RATIO }
    );
    onReady?.();
  };
  // If the sprite fails to load, leave nothing registered — the icon layer just
  // won't draw, rather than pointing at a missing image.
  img.onerror = () => {};
  img.src = iconUrl;
};

const ensureCatchmentMarkerLayers = (map: MaplibreMap) => {
  if (!map.getLayer(CATCHMENT_MARKER_ICON_LAYER_ID)) {
    map.addLayer({
      id: CATCHMENT_MARKER_ICON_LAYER_ID,
      type: "symbol",
      source: CATCHMENT_MARKER_SOURCE_ID,
      layout: {
        "icon-image": CATCHMENT_MARKER_IMAGE_ID,
        "icon-size": 1,
        "icon-anchor": "bottom",
        "icon-allow-overlap": true,
        "icon-ignore-placement": true
      }
    });
  }

  if (!map.getLayer(CATCHMENT_MARKER_LABEL_LAYER_ID)) {
    map.addLayer({
      id: CATCHMENT_MARKER_LABEL_LAYER_ID,
      type: "symbol",
      source: CATCHMENT_MARKER_SOURCE_ID,
      layout: {
        "symbol-placement": "point",
        "text-field": ["coalesce", ["get", "label"], ""],
        "text-font": ["Open Sans Bold", "Arial Unicode MS Regular"],
        "text-size": 12,
        "text-anchor": "bottom",
        "text-offset": [0, LABEL_TEXT_OFFSET_Y],
        "text-max-width": 8,
        "text-allow-overlap": true,
        "text-ignore-placement": true,
        "text-pitch-alignment": "viewport",
        "text-rotation-alignment": "viewport"
      },
      paint: {
        "text-color": LABEL_TEXT_COLOR,
        "text-halo-color": LABEL_HALO_COLOR,
        "text-halo-width": 1.2,
        "text-opacity": 1
      }
    });
  }

  if (!map.getLayer(CATCHMENT_MARKER_RANK_LAYER_ID)) {
    map.addLayer({
      id: CATCHMENT_MARKER_RANK_LAYER_ID,
      type: "symbol",
      source: CATCHMENT_MARKER_SOURCE_ID,
      filter: ["has", "rank"],
      layout: {
        "symbol-placement": "point",
        "text-field": ["get", "rank"],
        "text-font": ["Open Sans Bold", "Arial Unicode MS Regular"],
        "text-size": 12,
        "text-anchor": "bottom",
        "text-offset": [0, RANK_TEXT_OFFSET_WITH_LABEL],
        "text-allow-overlap": true,
        "text-ignore-placement": true,
        "text-pitch-alignment": "viewport",
        "text-rotation-alignment": "viewport"
      },
      paint: {
        "text-color": LABEL_TEXT_COLOR,
        "text-halo-color": LABEL_HALO_COLOR,
        "text-halo-width": 1.2,
        "text-opacity": 1
      }
    });
  }
};

/** Shows/hides the label layer, shifting the rank down into the label's slot when hidden. */
export const setCatchmentMarkerLabelsVisibility = (
  map: MaplibreMap | null,
  visible: boolean
) => {
  if (!map) return;
  if (map.getLayer(CATCHMENT_MARKER_LABEL_LAYER_ID)) {
    map.setLayoutProperty(
      CATCHMENT_MARKER_LABEL_LAYER_ID,
      "visibility",
      visible ? "visible" : "none"
    );
  }
  if (map.getLayer(CATCHMENT_MARKER_RANK_LAYER_ID)) {
    map.setLayoutProperty(CATCHMENT_MARKER_RANK_LAYER_ID, "text-offset", [
      0,
      visible ? RANK_TEXT_OFFSET_WITH_LABEL : RANK_TEXT_OFFSET_WITHOUT_LABEL
    ]);
  }
};

/**
 * Shows/hides all marker layers in one call. When shown, the label honours the
 * current `showLabel` setting rather than being forced visible.
 */
export const setCatchmentMarkersVisibility = (
  map: MaplibreMap | null,
  visible: boolean,
  showLabel: boolean
) => {
  if (!map) return;
  const iconVisibility = visible ? "visible" : "none";

  if (map.getLayer(CATCHMENT_MARKER_ICON_LAYER_ID)) {
    map.setLayoutProperty(
      CATCHMENT_MARKER_ICON_LAYER_ID,
      "visibility",
      iconVisibility
    );
  }
  if (map.getLayer(CATCHMENT_MARKER_RANK_LAYER_ID)) {
    map.setLayoutProperty(
      CATCHMENT_MARKER_RANK_LAYER_ID,
      "visibility",
      iconVisibility
    );
  }
  // Label visibility AND the rank text-offset are both owned by this helper.
  setCatchmentMarkerLabelsVisibility(map, visible && showLabel);
};

/** Renders the dataset markers as a single GeoJSON source + symbol layers. */
export const renderCatchmentMarkers = (
  map: MaplibreMap | null,
  iconUrl: string,
  items: CatchmentMarkerFeatureInput[],
  showLabel: boolean
) => {
  if (!map) return;

  const data = buildCatchmentMarkerFeatureCollection(items);
  const source = map.getSource<GeoJSONSource>(CATCHMENT_MARKER_SOURCE_ID);

  if (source) {
    source.setData(data);
  } else {
    map.addSource(CATCHMENT_MARKER_SOURCE_ID, { type: "geojson", data });
  }

  const applyLayers = () => {
    // The source can be torn down (removeCatchmentMarkers) between the async
    // image load and this callback — bail if it's gone.
    if (!map.getSource(CATCHMENT_MARKER_SOURCE_ID)) return;
    ensureCatchmentMarkerLayers(map);
    setCatchmentMarkerLabelsVisibility(map, showLabel);
  };

  if (map.hasImage?.(CATCHMENT_MARKER_IMAGE_ID)) {
    applyLayers();
  } else {
    registerCatchmentMarkerImage(map, iconUrl, applyLayers);
  }
};

/** Removes the marker layers and source, if present. */
export const removeCatchmentMarkers = (map: MaplibreMap | null) => {
  // Layers and source in MapLibre are only available after the map's style is ready
  if (!map?.isStyleLoaded()) return;
  [
    CATCHMENT_MARKER_ICON_LAYER_ID,
    CATCHMENT_MARKER_LABEL_LAYER_ID,
    CATCHMENT_MARKER_RANK_LAYER_ID
  ].forEach((layerId) => {
    if (map.getLayer(layerId)) map.removeLayer(layerId);
  });
  if (map.getSource(CATCHMENT_MARKER_SOURCE_ID)) {
    map.removeSource(CATCHMENT_MARKER_SOURCE_ID);
  }
};
